import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, MapPin, Clock, Infinity, Users, Eye, Sparkles, ShieldCheck, Flag, Star, Sun } from "lucide-react";
import { BANNER, GOOGLE_REVIEWS, SITE } from "@/data/site";

const HILUX_IMG = "/vehicles/hilux/1.jpg";
const KLUGER_IMG = "/vehicles/kluger/1.jpg";

export default function Home() {
  return (
    <div>
      {BANNER.enabled && (
        <Link to={BANNER.href} className="block bg-[#BA5A3A] text-[#F7F5F0] hover:bg-[#a34e32] transition-colors" data-testid="season-banner">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3 text-sm font-medium">
            <Sun className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{BANNER.text}</span>
            <span className="inline-flex items-center gap-1 font-black underline underline-offset-4">{BANNER.cta} <ArrowUpRight className="w-4 h-4" /></span>
          </div>
        </Link>
      )}
      {/* Hero split */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-6">
          <div className="overline text-[#BA5A3A]">Darwin · Northern Territory</div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mt-3 max-w-4xl">
            Straight rates. <br/>
            <span className="text-[#BA5A3A]">Built for the Top End.</span>
          </h1>
          <p className="mt-6 text-lg text-[#595959] max-w-2xl">
            Independent, Territory-owned. Straight rates, no airport fees hidden, bond and excess on the page — not in the fine print.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-0 border-t border-black/10">
          <PathCard
            href="/vehicles/hilux"
            tag="Adventure"
            title="Toyota Hilux SR 4x4"
            copy="Full recovery kit. Roof rack. Fridge socket. Kakadu and Litchfield-ready."
            img={HILUX_IMG}
            testId="path-hilux"
            accent
          />
          <PathCard
            href="/vehicles/kluger"
            tag="Premium Family"
            title="Toyota Kluger Grande"
            copy="7-seat hybrid. Leather. CarPlay. School run, weddings, or the airport."
            img={KLUGER_IMG}
            testId="path-kluger"
          />
        </div>
      </section>

      {/* Feature promises strip */}
      <section className="border-b border-black/10" data-testid="promises-strip">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="overline text-[#BA5A3A]">Our promise</div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight mt-2">What you get, every hire</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-10">
            <Promise icon={Infinity} title="Unlimited KMs" body="Drive as far as your permitted zone allows — no daily cap, no per-km fees."/>
            <Promise icon={Users} title="No extra driver charges" body="Add a second driver on the same booking at no extra cost — just show us their licence."/>
            <Promise icon={Eye} title="No hidden costs" body="Bond, excess, cleaning and refuel fees are on the vehicle page — never sprung at pickup."/>
            <Promise icon={Sparkles} title="Vehicles in superb condition" body="Both fleet vehicles are serviced regularly, cleaned inside and out, and inspected before every hire."/>
            <Promise icon={ShieldCheck} title="Liability reduction available" body="Optional excess reduction lowers your bond and out-of-pocket liability."/>
            <Promise icon={Flag} title="Locally owned & operated" body="Territory-owned and Darwin-based. You'll deal with the owner directly, every hire."/>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
        <TrustCard icon={Shield} title="Bond and excess up-front"
          body="Standard bond A$3,000 (Kluger) / A$5,000 (Hilux), or just A$500 with excess reduction. In line with Darwin 4WD specialists. No mystery charges post-return."/>
        <TrustCard icon={MapPin} title="Territory-restricted, honestly"
          body="Hilux: Darwin + Litchfield + sealed Kakadu. Kluger: sealed roads. Anything else — enquire first."/>
        <TrustCard icon={Clock} title="Review inside 24 hours"
          body="Send us your dates — we confirm availability and email a secure deposit link once we've confirmed the vehicle."/>
      </section>

      {/* Where we operate — map */}
      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="where-we-operate">
        <div className="overline text-[#BA5A3A]">Where we operate</div>
        <h2 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Darwin base · Top End range</h2>
        <p className="text-[#595959] mt-3 max-w-2xl">Pickups from our Leanyer base — just 5–10 minutes from Darwin Airport (address disclosed after deposit). Permitted zones cover Darwin, Litchfield, Kakadu sealed sections and the Adelaide River corridor. <Link to="/where-to-go" className="text-[#BA5A3A] font-bold underline underline-offset-4" data-testid="home-trips-link">See our itineraries →</Link></p>
        <div className="mt-8 card-hard overflow-hidden">
          <iframe
            title="Top End Wheels — Darwin NT service area"
            src="https://www.google.com/maps?q=Leanyer+NT+0812+Australia&z=8&output=embed"
            width="100%" height="420" style={{ border: 0 }}
            allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade">
          </iframe>
        </div>
      </section>

      {/* Regional accreditation marks */}
      <section className="border-t border-black/10 bg-[#1C1C1C]" data-testid="territory-badges">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="overline text-[#BA5A3A]">Proudly of the Territory</div>
              <img
                src="/badges/territory-marks.png"
                alt="Territory Proud · Northern Territory Tourism NT · Tourism Australia"
                className="mt-6 max-w-full h-auto md:h-24 object-contain object-left"
                data-testid="territory-marks-image"
              />
            </div>
            <a href={SITE.google_business_url}
               target="_blank" rel="noreferrer"
               className="group block bg-white p-5 hover:shadow-[6px_6px_0_#BA5A3A] transition"
               data-testid="find-on-google">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex-shrink-0 rounded-full bg-[#F7F5F0] border border-black/10 flex items-center justify-center overflow-hidden">
                  <svg viewBox="0 0 32 32" className="w-8 h-8">
                    <path fill="#4285F4" d="M31.4 16.36c0-1.06-.09-2.08-.27-3.06H16v5.79h8.63a7.39 7.39 0 01-3.21 4.85v4.03h5.19c3.03-2.79 4.79-6.9 4.79-11.61z"/>
                    <path fill="#34A853" d="M16 32c4.32 0 7.94-1.43 10.61-3.87l-5.19-4.03c-1.44.97-3.28 1.54-5.42 1.54-4.16 0-7.69-2.81-8.95-6.59H1.7v4.14A16 16 0 0016 32z"/>
                    <path fill="#FBBC05" d="M7.05 19.05a9.63 9.63 0 010-6.1V8.81H1.7a16 16 0 000 14.38l5.35-4.14z"/>
                    <path fill="#EA4335" d="M16 6.36c2.35 0 4.46.81 6.12 2.4l4.6-4.6C23.93 1.59 20.31 0 16 0A16 16 0 001.7 8.81l5.35 4.14C8.31 9.17 11.84 6.36 16 6.36z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="overline text-[#595959]">Contact · Reviews</div>
                  <div className="font-black text-lg text-[#1C1C1C] group-hover:text-[#BA5A3A] transition">Find us on Google</div>
                  {GOOGLE_REVIEWS.rating > 0 && (
                    <div className="flex items-center gap-2 mt-1" data-testid="google-rating">
                      <span className="font-black text-[#1C1C1C]">{GOOGLE_REVIEWS.rating.toFixed(1)}</span>
                      <span className="flex gap-0.5">
                        {[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(GOOGLE_REVIEWS.rating) ? "fill-[#FBBC05] text-[#FBBC05]" : "text-[#c9c4b8]"}`} />)}
                      </span>
                      <span className="text-xs text-[#595959]">{GOOGLE_REVIEWS.count > 0 ? `${GOOGLE_REVIEWS.count} Google reviews` : "New on Google — be our first review"}</span>
                    </div>
                  )}
                  <div className="text-xs text-[#595959] mt-1">Opens our Google Business profile — leave a review, get directions, call →</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}

function PathCard({ href, tag, title, copy, img, accent, testId }) {
  return (
    <Link to={href} data-testid={testId}
      className={`group relative block h-[440px] md:h-[560px] overflow-hidden ${accent ? "border-r border-black/10" : ""}`}>
      <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/40 to-black/80"></div>
      <div className="relative h-full flex flex-col justify-end p-8 md:p-12 text-[#F7F5F0]">
        <div className="overline text-[#BA5A3A]">{tag}</div>
        <h3 className="text-3xl md:text-5xl font-black tracking-tighter mt-2">{title}</h3>
        <p className="mt-3 max-w-md text-white/85">{copy}</p>
        <div className="mt-6 flex items-center justify-end">
          <span className="inline-flex items-center gap-2 border border-white/60 px-4 py-2 group-hover:bg-white group-hover:text-[#1C1C1C] transition">
            Book this vehicle <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="card-hard p-8">
      <Icon className="w-6 h-6 text-[#BA5A3A]" />
      <h3 className="mt-4 text-xl font-black tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-[#595959] leading-relaxed">{body}</p>
    </div>
  );
}

function Promise({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-4" data-testid="promise-item">
      <div className="w-12 h-12 flex-shrink-0 bg-[#BA5A3A]/10 border border-[#BA5A3A]/25 flex items-center justify-center">
        <Icon className="w-6 h-6 text-[#BA5A3A]" strokeWidth={1.8}/>
      </div>
      <div>
        <h3 className="font-black text-base tracking-tight">{title}</h3>
        <p className="text-sm text-[#595959] leading-relaxed mt-1">{body}</p>
      </div>
    </div>
  );
}

